import { ref, computed, nextTick } from '@vue/composition-api'

export function useOtp({ length = 6 } = {}) {
  const otpDigits = ref(Array(length).fill(''))
  const isInteracting = ref(false)
  const blurTimeout = ref(null)

  const isOtpComplete = computed(() => otpDigits.value.every(d => d !== ''))

  const focusInput = index => {
    document.querySelector(`[data-otp-index="${index}"]`)?.focus()
  }

  async function handleInput(index) {
    const input = otpDigits.value[index] || ''
    if (!/^\d$/.test(input)) {
      otpDigits.value[index] = ''
      return
    }

    if (index < length - 1) focusInput(index + 1)
    else await nextTick(() => document.activeElement?.blur())
  }

  function shouldMoveFocus(event, index) {
    return event.key === 'Backspace' && index > 0 && !otpDigits.value[index]
  }

  function handleKeydown(event, index) {
    if (shouldMoveFocus(event, index)) {
      nextTick(() => focusInput(index - 1))
    }
  }

  function handlePaste(event) {
    event.preventDefault()
    const text = (event.clipboardData || window.clipboardData).getData('text').trim()
    if (!/^\d+$/.test(text)) return

    const digits = text.slice(0, length).split('')
    otpDigits.value = Array(length).fill('').map((_, i) => digits[i] || '')

    nextTick(() => {
      const targetIndex = Math.min(digits.length, length) - 1
      digits.length >= length
        ? document.activeElement?.blur()
        : focusInput(targetIndex + 1)
    })
  }

  function handleFocus() {
    if (blurTimeout.value) clearTimeout(blurTimeout.value)
    blurTimeout.value = null
    isInteracting.value = true
  }

  function handleBlur() {
    blurTimeout.value = setTimeout(() => {
      if (!document.activeElement?.hasAttribute('data-otp-index')) {
        isInteracting.value = false
      }
    }, 100)
  }

  function resetOtp() {
    otpDigits.value = Array(length).fill('')
    isInteracting.value = false
    if (blurTimeout.value) clearTimeout(blurTimeout.value)
    blurTimeout.value = null
    nextTick(() => focusInput(0))
  }

  return {
    otpDigits,
    isOtpComplete,
    isInteracting,
    handleInput,
    handleKeydown,
    handlePaste,
    handleFocus,
    handleBlur,
    resetOtp,
  }
}
